/**
 * Apollo Client reactive variables for player kanban feature
 *
 * Provides global state management using Apollo Client's reactive variables
 * for the player kanban board sidebar and other UI state.
 */

import { makeVar } from "@apollo/client";

/**
 * Reactive variable for the currently selected player ID in the sidebar
 * @remarks
 * This replaces the legacy PlayerSidebarService event emitter pattern.
 * Use the usePlayerSidebar hook to access and modify this state.
 *
 * Initial value is null (no player selected)
 */
export const selectedPlayerIdVar = makeVar<string | null>(null);

/**
 * Reactive variable for the active kanban board ID
 * @remarks
 * Persists the selected board across navigation (e.g., when navigating
 * to a player page and returning to the Boards tab). Without this,
 * the board selection resets to the first board on remount.
 */
export const activeBoardIdVar = makeVar<string | null>(null);

/**
 * Reactive variable for fallback tag colors across boards
 * @remarks
 * Stores a mapping of tagId -> color that persists across the session.
 * When a tag is created with a color on one board, this stores the color
 * so it can be used as a fallback when viewing the tag on other boards
 * that don't have a board-specific color set.
 *
 * Priority for tag colors:
 * 1. Board-specific color (from KanbanBoardTag)
 * 2. Fallback color (from this reactive variable)
 * 3. DEFAULT_TAG_COLOR
 */
export const tagFallbackColorsVar = makeVar<Record<string, string>>({});

/**
 * Set a fallback color for a tag
 * @param tagId - The ID of the tag
 * @param color - The color to store as fallback
 * @remarks
 * Call this when creating a tag or when a user selects a color for a tag.
 * The color will be available as a fallback on other boards.
 */
export function setTagFallbackColor(tagId: string, color: string): void {
  const currentColors = tagFallbackColorsVar();
  tagFallbackColorsVar({ ...currentColors, [tagId]: color });
}

/**
 * Get the fallback color for a tag
 * @param tagId - The ID of the tag
 * @returns The fallback color or undefined if not set
 */
export function getTagFallbackColor(tagId: string): string | undefined {
  return tagFallbackColorsVar()[tagId];
}

/**
 * Bulk update fallback colors from a map of tagId -> color
 * @param colors - Record of tagId to color mappings
 * @remarks
 * This is used to populate fallback colors when loading board tags.
 * Only adds colors for tags that don't already have a fallback color,
 * preserving user-set colors from the current session.
 */
export function populateFallbackColors(colors: Record<string, string>): void {
  const currentColors = tagFallbackColorsVar();
  const newColors = Object.entries(colors).reduce(
    (acc, [tagId, color]) => {
      // Only add if we don't already have a fallback for this tag
      if (!acc[tagId] && color) {
        return { ...acc, [tagId]: color };
      }
      return acc;
    },
    { ...currentColors }
  );
  tagFallbackColorsVar(newColors);
}

/**
 * Reset fallback colors to empty state
 * @remarks
 * This is primarily used for testing to ensure a clean state between tests.
 */
export function resetTagFallbackColors(): void {
  tagFallbackColorsVar({});
}
